import { useState } from 'react';
import { createApiClient, availabilityService } from '../services/api';
import { AvailabilityKind, AVAILABILITY_KIND_LABELS } from '../types';
import type { AvailabilityDto } from '../types';

// ─────────────────────────────────────────────
//  PROVIDER TIME OFF
//
//  Lives beside the work calendar rather than on its own screen: the question a
//  provider is answering — "am I free that week?" — is the same question the
//  calendar already shows them.
// ─────────────────────────────────────────────

interface AddTimeOffModalProps {
  getToken: () => Promise<string>;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function AddTimeOffModal({ getToken, onClose, onSaved }: AddTimeOffModalProps) {
  const today = new Date().toISOString().slice(0, 10);

  const [kind, setKind]           = useState<AvailabilityKind>(AvailabilityKind.Holiday);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate]     = useState(today);
  const [allDay, setAllDay]       = useState(true);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour]     = useState(17);
  const [capacity, setCapacity]   = useState('');
  const [note, setNote]           = useState('');
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const year = new Date(startDate).getFullYear();

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      await availabilityService(api).add({
        kind,
        startDate,
        endDate,
        isAllDay: allDay,
        startHour: allDay ? null : startHour,
        endHour:   allDay ? null : endHour,
        capacityOverride: capacity === '' ? null : parseInt(capacity, 10),
        note: note.trim() || null,
      });
      await onSaved();
    } catch (e) {
      const body = (e as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
      setError(body?.message ?? body?.detail ?? 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  const blockBankHolidays = async () => {
    setBusy(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      const { added } = await availabilityService(api).blockBankHolidays(year);
      // Nothing added means they were already blocked, which is a fine outcome but a
      // confusing one to report as success — the calendar would look unchanged.
      if (added === 0) setError(`Bank holidays in ${year} are already blocked.`);
      else await onSaved();
    } catch {
      setError('Could not block the bank holidays.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto"
        data-testid="calendar-add-block-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Add time off"
      >
        <h3 className="font-semibold text-gray-900 mb-1">Add time off</h3>
        <p className="text-xs text-gray-500 mb-4">
          You won&apos;t be auto-quoted for work on these dates, and they&apos;ll be shaded on
          your calendar.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <label className="block mb-3">
          <span className="text-xs font-medium text-gray-600">Reason</span>
          <select
            value={kind}
            onChange={e => setKind(parseInt(e.target.value, 10) as AvailabilityKind)}
            data-testid="add-block-kind"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value={AvailabilityKind.Holiday}>Holiday</option>
            <option value={AvailabilityKind.Maintenance}>Maintenance</option>
            <option value={AvailabilityKind.CustomBlock}>Other</option>
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">From</span>
            <input
              type="date"
              value={startDate}
              data-testid="add-block-start"
              onChange={e => {
                setStartDate(e.target.value);
                // Keep the range valid as the user types rather than rejecting it on save.
                if (e.target.value > endDate) setEndDate(e.target.value);
              }}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">To</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              data-testid="add-block-end"
              onChange={e => setEndDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>
        </div>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={allDay}
            onChange={e => setAllDay(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600"
          />
          <span className="text-sm text-gray-700">All day</span>
        </label>

        {!allDay && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600">From (hour)</span>
              <input
                type="number" min={0} max={23} value={startHour}
                onChange={e => setStartHour(parseInt(e.target.value, 10) || 0)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">To (hour)</span>
              <input
                type="number" min={1} max={24} value={endHour}
                onChange={e => setEndHour(parseInt(e.target.value, 10) || 0)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </label>
          </div>
        )}

        <label className="block mb-3">
          <span className="text-xs font-medium text-gray-600">
            Or cap jobs that day instead of blocking it
          </span>
          <input
            type="number" min={0} value={capacity}
            placeholder="Leave blank to block completely"
            onChange={e => setCapacity(e.target.value)}
            data-testid="add-block-capacity"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
          <span className="text-[11px] text-gray-400 mt-1 block">
            A cap keeps you available — you just stop being auto-quoted once you hit it.
          </span>
        </label>

        <label className="block mb-4">
          <span className="text-xs font-medium text-gray-600">Note (optional)</span>
          <input
            value={note} maxLength={200}
            onChange={e => setNote(e.target.value)}
            placeholder="Annual leave"
            data-testid="add-block-note"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </label>

        {/* Nobody is going to type eight dates a year, and a provider who skips them gets
            auto-quoted on Christmas Day. */}
        <button
          type="button"
          onClick={() => void blockBankHolidays()}
          disabled={busy}
          data-testid="add-block-bank-holidays"
          className="w-full mb-3 px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Or block every bank holiday in {year}
        </button>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={() => void save()}
            disabled={busy}
            data-testid="add-block-save"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

interface RemoveTimeOffModalProps {
  block: AvailabilityDto;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

export function RemoveTimeOffModal({ block, onCancel, onConfirm }: RemoveTimeOffModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5"
        data-testid="calendar-remove-block"
        role="dialog"
        aria-modal="true"
      >
        <h3 className="font-semibold text-gray-900">Remove this time off?</h3>
        <p className="text-sm text-gray-500 mt-1">
          {AVAILABILITY_KIND_LABELS[block.kind]} · {block.startDate}
          {block.endDate !== block.startDate && ` to ${block.endDate}`}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          You&apos;ll start being auto-quoted for work on these dates again.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">
            Keep it
          </button>
          <button
            onClick={() => void onConfirm()}
            data-testid="calendar-remove-block-confirm"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}
